import React, { FC, useState, useEffect } from "react";
import { Box, Typography, Modal, IconButton } from "@mui/material";
import { HighlightOff } from "@mui/icons-material";
import { Colors } from "../../../configs";
import { CustomButton, CustomTextBox } from "../../atoms";
import { pxToRem } from "../../../util";

interface PDFDownloadAccessModalProps {
  open: boolean;
  onClose: () => void;
  onAccessGranted: () => void;
}

const PDFDownloadAccessModal: FC<PDFDownloadAccessModalProps> = ({
  open,
  onClose,
  onAccessGranted,
}) => {
  const [accessCode, setAccessCode] = useState("");
  const [accessError, setAccessError] = useState(false);

  useEffect(() => {
    if (!open) {
      setAccessCode("");
      setAccessError(false);
    }
  }, [open]);

  const handleDone = () => {
    if (accessCode === "1234") {
      setAccessError(false);
      onAccessGranted();
      onClose();
    } else {
      setAccessError(true);
    }
  };

  const handleClose = (
    event: {},
    reason: "backdropClick" | "escapeKeyDown" | "closeButtonClick"
  ) => {
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        zIndex: 1300,
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          py: "3.49321429rem",
          px: "3.57142857rem",
          borderRadius: "0.3571428571428571rem",
          width: pxToRem(600),
          height: pxToRem(430),
        }}
      >
        {/* Header with title and close button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "1rem",
          }}
        >
          <Typography
            id="modal-modal-title"
            variant="h4"
            component="h2"
            sx={{
              fontWeight: "bold",
              color: Colors.TEXT_GRAY_DARK,
            }}
          >
            Download
          </Typography>
          <IconButton
            aria-label="close"
            test-name="close-button"
            onClick={() => handleClose({}, "closeButtonClick")}
            sx={{
              alignSelf: "flex-start",
              width: "2rem",
              height: "2rem",
              padding: "0",
            }}
          >
            <HighlightOff sx={{ fontSize: "2.5rem" }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            paddingTop: "1rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <Typography
            sx={{
              color: Colors.TEXT_GRAY_DARK,
              fontSize: pxToRem(20),
              lineHeight: 1.4,
              textAlign: "center",
            }}
          >
            Please enter the access code before downloading{" "}
            <strong>RP5332.pdf</strong> !
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                color: Colors.TEXT_GRAY_DARK,
                fontSize: pxToRem(20),
                marginBottom: pxToRem(5),
                marginLeft: pxToRem(60),
              }}
            >
              Enter Access Code:
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CustomTextBox
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                sx={{ width: "75%", height: pxToRem(50) }}
                placeholder=""
                error={accessError}
                helperText={accessError ? "Invalid access code." : ""}
                inputProps={{
                  style: {
                    fontSize: pxToRem(20),
                    height: pxToRem(39),
                    padding: "0 1rem",
                  },
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              gap: "1rem",
              marginTop: "1.5rem",
            }}
          >
            <CustomButton
              sx={{
                height: pxToRem(64),
                minWidth: pxToRem(467),
                flex: 1,
                fontSize: "1.42857143rem",
                fontWeight: "bold",
                padding: "0",
              }}
              onClick={handleDone}
              disabled={!accessCode.trim()}
            >
              Done
            </CustomButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default PDFDownloadAccessModal;